/* eslint-disable no-unused-vars */
import React, { Fragment, useEffect, useState } from 'react'
import ElementContainer from '../ElementContainer'
import { Box, Button, Input, Typography, Divider } from '@mui/material'
import { authorizationFieldStyles } from '../../../styles/classes/MobilePreviewClasses'
import { useTranslation } from 'react-i18next'
import { faFileContract } from '@fortawesome/pro-light-svg-icons'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment/moment'
import {
  requiredAuthorizationOptions,
  requiredAuthorizationOptionsObj
} from '../../../utils/formBuilder'

const AuthorizationField = ({
  helpfulHint,
  answer,
  onChangeValue,
  required,
  question,
  isPreviewMode,
  onPreviewModeSwitch
}) => {
  const [localTitle, setLocalTitle] = useState(answer?.title)
  const [localUrl, setLocalUrl] = useState(answer?.url)
  const { classes } = authorizationFieldStyles()
  const { t } = useTranslation()

  const repairDateCreated = moment().valueOf() / 1000 // epoch date
  const paymentTerms = question.payment_terms
  const authorizatioToProceed = question.authorization_to_proceed

  const enabledSignature = required !== requiredAuthorizationOptionsObj.DISABLE
  const nameRequired =
    required === requiredAuthorizationOptionsObj.SIGNATURE_AND_NAME_REQUIRED

  const imageChangeHandler = e => {
    const files = [...e.target.files]
    const file = files[0]
    const imageUrl = URL.createObjectURL(file)
    setLocalUrl(imageUrl)
    onChangeValue({ ...answer, url: imageUrl, title: localTitle })
  }

  const titleChangeHandler = e => {
    setLocalTitle(e.target.value)
  }

  const blurHandler = () => {
    onChangeValue({ title: localTitle, url: localUrl })
  }

  function transformString (input) {
    // Extract the number using a regular expression
    const match = input.match(/\d+/)
    const number = match ? parseInt(match[0], 10) : null

    // Format the string: capitalize the first word and replace underscores with spaces
    const formattedString = input
      .replace('_', ' ')
      .replace(/^\w/, c => c.toUpperCase())

    // Return both the formatted string and the number in an object
    return {
      string: formattedString,
      number
    }
  }

  function calculatePaymentDue (epochDate, daysToAdd) {
    // times 1000 to transform seconds to milliseconds
    const dueDate = moment(epochDate * 1000).add(daysToAdd, 'days')
    // return formatted string
    return dueDate.format('MMM D, YYYY')
  }

  const inputImage = () => {
    return (
      <input
        hidden
        accept="image/png, image/jpeg, image/jpg, image/bitmap"
        type="file"
        onChange={imageChangeHandler}
      />
    )
  }

  const title = (
    <Box className={classes.titleContainer}>
      {!isPreviewMode && (
        <FontAwesomeIcon
          icon={faFileContract}
          size="2x"
          className={classes.fileIcon}
        />
      )}
      <Typography className={classes.title}>
        {isPreviewMode
          ? t('mobile_preview.authorization_field.authorization')
          : t('mobile_preview.authorization_field.review_and_authorization')}
      </Typography>
    </Box>
  )

  return (
    <Fragment>
      <ElementContainer
        title={title}
        helpfulHint={helpfulHint}
        applyShadow={isPreviewMode}
      >
        {isPreviewMode && (
          <Divider component="li" className={classes.divider} />
        )}
        {enabledSignature && !localUrl && (
          <Fragment>
             <Box className={classes.subtitleContainer}>
              {required !== requiredAuthorizationOptions.DISABLE && (
                <Typography className={classes.asterisk}>*</Typography>
              )}
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.signature')}
              </Typography>
            </Box>
          <Button
            fullWidth
            component="label"
            className={classes.selectButtonAlt}
          >
            {inputImage()}
            <Typography className={classes.selectButtonText}>
              {t('mobile_preview.authorization_field.sign')}
            </Typography>
          </Button>
          </Fragment>
        )}
        {enabledSignature && localUrl && (
          <Fragment>
            <Box className={classes.subtitleContainer}>
              {required !== requiredAuthorizationOptions.DISABLE && (
                <Typography className={classes.asterisk}>*</Typography>
              )}
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.signature')}
              </Typography>
            </Box>
            {!localUrl && (
              <Button
                fullWidth
                component="label"
                className={classes.placeholderContainer}
              >
                {inputImage()}
                <Typography className={classes.placeholderText}>
                  {t('mobile_preview.signature.sign_here')}
                </Typography>
              </Button>
            )}
            {localUrl && (
              <Button fullWidth component="label">
                {inputImage()}
                <img
                  className={classes.signatureImage}
                  src={localUrl}
                  srcSet={localUrl}
                  loading="lazy"
                />
              </Button>
            )}
            <Box className={classes.subtitleContainer}>
              {nameRequired && (
                <Typography className={classes.asterisk}>*</Typography>
              )}
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.name')}
              </Typography>
            </Box>

            <Input
              sx={{ marginLeft: '5px' }}
              fullWidth
              className={classes.textInput}
              size="small"
              disableUnderline
              onBlur={blurHandler}
              onChange={titleChangeHandler}
              value={localTitle}
              placeholder={t('mobile_preview.signature.enter_name_here')}
            />
          </Fragment>
        )}

        {paymentTerms === 'due_on_receipt' && (
          <Box className={classes.paymentTermsContainer}>
            <Box>
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.payment_terms')}
              </Typography>
              <Input
                fullWidth
                className={classes.textInput}
                size="small"
                disableUnderline
                onChange={titleChangeHandler}
                value={t('mobile_preview.authorization_field.due_on_receipt')}
                placeholder={t('mobile_preview.signature.enter_name_here')}
              />
            </Box>
          </Box>
        )}
        {paymentTerms !== 'disable' && paymentTerms !== 'due_on_receipt' && (
          <Box className={classes.paymentTermsContainer}>
            <Box>
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.payment_terms')}
              </Typography>
              <Input
                disabled
                fullWidth
                className={classes.textInput}
                size="small"
                disableUnderline
                onChange={titleChangeHandler}
                value={transformString(paymentTerms).string}
                placeholder={t('mobile_preview.signature.enter_name_here')}
              />
            </Box>
            <Box>
              <Typography className={classes.subTitle}>
                {t('mobile_preview.authorization_field.payment_due')}
              </Typography>
              <Input
                disabled
                fullWidth
                className={classes.textInput}
                size="small"
                disableUnderline
                onChange={titleChangeHandler}
                value={calculatePaymentDue(
                  repairDateCreated,
                  +transformString(paymentTerms).number
                )}
                placeholder={t('mobile_preview.signature.enter_name_here')}
              />
            </Box>
          </Box>
        )}

        {authorizatioToProceed && !isPreviewMode && (
          <Fragment>
            <Typography className={classes.subTitle}>
              {t('mobile_preview.authorization_field.authorization_to_proceed')}
            </Typography>
            <Box className={classes.authorizationToProceedContainer}>
              <Input
                disabled
                fullWidth
                className={classes.textInput}
                size="small"
                disableUnderline
                onChange={titleChangeHandler}
                value={
                  localUrl
                    ? t('mobile_preview.authorization_field.work_authorized')
                    : t('mobile_preview.authorization_field.not_authorized')
                }
                placeholder={t('mobile_preview.signature.enter_name_here')}
              />
              <FontAwesomeIcon
                icon={faChevronDown}
                size="1x"
                className={classes.fileIcon}
              />
            </Box>
          </Fragment>
        )}

        {authorizatioToProceed && isPreviewMode && (
          <Box>
            <Typography className={classes.subTitle}>
              {t('mobile_preview.authorization_field.authorization_to_proceed')}
            </Typography>
            <div className={classes.buttonSeparator} />
            <Box className={classes.selectButtonAlt}>
              <Typography className={classes.selectButtonTextAlt}>
                {localUrl
                  ? `${t(
                      'mobile_preview.authorization_field.work_authorized'
                    )}!`
                  : t('mobile_preview.authorization_field.not_authorized')}
              </Typography>
            </Box>
          </Box>
        )}

        {!isPreviewMode && (
          <Box>
            <Typography className={classes.subTitle}>
              {t('mobile_preview.authorization_field.review_and_authorize')}
            </Typography>
            <div className={classes.buttonSeparator} />
            <Box
              className={classes.selectButtonAlt}
              onClick={onPreviewModeSwitch}
            >
              <Typography className={classes.selectButtonText}>
                {t('mobile_preview.authorization_field.generate_review')}
              </Typography>
            </Box>
          </Box>
        )}
      </ElementContainer>
      <Box className={classes.botttomSpace} />
      {!isPreviewMode && <Divider component="li" className={classes.divider} />}
    </Fragment>
  )
}

export default AuthorizationField
